import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as IconOutline } from "../../../images/icon-outline.svg";
import { ReactComponent as RippleLoading } from "../../../images/ripple-loading.svg";
import { ChipGroup, Heading } from "cai-fusion";
import { useNewTemplate } from "../contexts/NewTemplateContext";
import SymphonyChatInput from "../components/symphonyChatInput";
import env from "../../../configs/envConfig";
import { debugLog } from "../../../configs/loggingConfig";

const searchIcon = require("../../../images/search-icon-light.png");

// Search bar for templates that filters from description and title, also by tag
const TemplateSearch = ({ onSearchTemplates }) => {
    const [searchText, setSearchText] = useState("");
    const searchTimeoutRef = useRef();

    const handleInputChange = (e) => {
        setSearchText(e.target.value);

        // Set a new timeout
        searchTimeoutRef.current = setTimeout(() => {
            handleSend(e.target.value);
        }, 200);
    };

    const handleKeyPress = (event) => {
        debugLog.log("[CHATTEMPLATE] KEY PRESSED");
        if (event.key === "Enter") {
            if (!event.shiftKey) {
                event.preventDefault();
                handleIconPress(event);
            }
        }
    };

    const handleIconPress = (event) => {
        event.preventDefault();
        handleSend(searchText);
    }

    const handleSend = (query) => {
        onSearchTemplates(query);
    };

    useEffect(() => {
        debugLog.log("ChatTemplate.jsx: searchText changed: " + searchText);
    }, [searchText]);

    return (
        <div>
            <form className="a-search-input l-mb-xs">
                <textarea
                    className="a-search-input__text-box"
                    type="text"
                    placeholder="Search for a chat template..."
                    aria-label="Search"
                    id="search_field"
                    rows="1"
                    name="search_field"
                    value={searchText}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                ></textarea>
                <button className="a-search-input__button" type="submit" onClick={handleIconPress}>
                    <img
                        className="a-search-input__img"
                        src={searchIcon}
                        alt="search button icon">
                    </img>
                </button>
            </form>
        </div>
    );
};

const ChatTemplateItem = ({
    IconElement,
    templateId,
    templateName,
    templateDescription,
    templateCallToAction,
    onClick
}) => {

    return (
        <button className="m-tile o-tile-grid__tile" onClick={onClick} key={templateId ?? templateName}>
            <div className="m-tile__icon-wrapper">
                {IconElement && <IconElement className="m-tile__icon" />}
            </div>
            <Heading size={5} className="m-tile__title">
                {templateName}
            </Heading>
            <p className="m-tile__description">{templateDescription}</p>
            <span className="m-tile__cta">{templateCallToAction}</span>
        </button>
    );

};

const SymphonyNewChatPage = () => {
    const { allChatTemplates, chatTemplate, setChatTemplate, chatTemplates, searchTemplates, showRFPItem, executeTemplate, fetchTemplate, isLoading, setIsLoading } = useNewTemplate();

    const [tags, setTags] = useState(["All Chat Templates", "Favorites"]);
    const [selectedTag, setSelectedTag] = useState("All Chat Templates");
    const [filteredTemplates, setFilteredTemplates] = useState(chatTemplates);

    const navigate = useNavigate();

    useEffect(() => {
        setChatTemplate(undefined);
    }, [])

    useEffect(() => {
        debugLog.log("[CHATTEMPLATE] Selected template changed to: ", chatTemplate);
    }, [chatTemplate])

    const getSortedTagsByFrequency = (data) => {
        const tagCount = {};

        // Count occurrences of each tag
        data.forEach(item => {
            item?.tags?.forEach(tag => {
                const tagName = tag;
                if (tagCount[tagName]) {
                    tagCount[tagName]++;
                } else {
                    tagCount[tagName] = 1;
                }
            });
        });

        // Convert to an array of [tagName, count] pairs and sort by count
        const sortedTags = Object.entries(tagCount).sort((a, b) => b[1] - a[1]);

        // Extract the tag names
        return sortedTags.map(([tagName]) => tagName);
    }

    useEffect(() => {
        setTags(["All Chat Templates", "Favorites", ...getSortedTagsByFrequency(allChatTemplates)])
    }, [allChatTemplates]);

    useEffect(() => {
        if (selectedTag === "All Chat Templates") {
            setFilteredTemplates(chatTemplates);
        } else if (selectedTag === "Favorites") {
            setFilteredTemplates(chatTemplates.filter(template => template.isFavorited));
        } else {
            setFilteredTemplates(chatTemplates.filter(template => template.tags && template.tags.includes(selectedTag)));
        }
    }, [selectedTag, chatTemplates]);

    const handleTagSelect = (tagName) => {
        setSelectedTag(tagName);
    }

    const handleTemplateSelect = async (template) => {
        setIsLoading(true);
        try {
            const respTemplate = await fetchTemplate(template.id);
            // Handling setting default values for selects. This should in theory be pre-given, but this logic will ensure it.
            for (let componentIndex = 0; componentIndex < respTemplate?.messageComponents.length; componentIndex++) {
                if (respTemplate?.messageComponents[componentIndex]?.componentType === "select") {
                    respTemplate.messageComponents[componentIndex].value = respTemplate.messageComponents[componentIndex].defaultValue
                }
            }
            setChatTemplate({...respTemplate});
            navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/chat/templates/${template.id}`)
        } catch (error) {
            debugLog.log("[CHATTEMPLATE] Error loading template:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRFPSelect = () => {
        setChatTemplate({});
        navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/chat/templates/rfp`)
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <Heading size={2}>{env.REACT_APP_ALLEGRO_GREETING_MESSAGE ?? `Choose a template or type a message`}</Heading>
                </div>
                <div className="col-md-6">
                    {env.REACT_APP_ALLEGRO_HIDE_TEMPLATE_SEARCH !== "true" &&
                        <TemplateSearch onSearchTemplates={(message) => searchTemplates(message)} />
                    }
                </div>
            </div>

            {env.REACT_APP_ALLEGRO_HIDE_TEMPLATE_SEARCH !== "true" &&
                <ChipGroup isSelectable selected={selectedTag} chipTexts={tags} onClose={handleTagSelect} />
            }

            {isLoading && (
                <div className="text-center l-mt-sm">
                    <RippleLoading className="a-icon__img" style={{maxHeight: 50}}/>
                    <p className="a-muted-text">Loading template...</p>
                </div>
            )}

            <div className="o-tile-grid l-mt-sm l-pb-xl" style={{opacity: isLoading ? 0.5 : 1, pointerEvents: isLoading ? 'none' : 'auto'}}>
                {filteredTemplates.map((template, index = 0) => (
                    <ChatTemplateItem
                        key={index}
                        IconElement={IconOutline}
                        templateId={index + 1}
                        templateName={template.title}
                        templateDescription={template.description}
                        templateCallToAction={"Get Started"}
                        onClick={() => { handleTemplateSelect(template) }}
                    />
                ))}
                {/* {(selectedTag === "All Chat Templates") && (window.location.href.indexOf("cai") > -1 || window.location.href.indexOf("localhost") > - 1) && showRFPItem && (<>
                    <ChatTemplateItem
                        key="-1"
                        IconElement={IconOutline}
                        templateId={filteredTemplates.length + 1}
                        templateName={"Perform a Standard RFP Review"}
                        templateDescription={"Analyze an RFP and get a first review on the document's details."}
                        templateCallToAction={"Get Started"}
                        onClick={() => { handleRFPSelect() }}
                    />
                </>)} */}
                {filteredTemplates.length === 0 && !showRFPItem &&
                    <p className="a-muted-text text-center l-mt-sm">Sorry! There are no matching templates for this criteria.</p>
                }
            </div>
            {env.REACT_APP_ALLEGRO_HIDE_NEW_CHAT_INPUT_BOX !== "true" && <SymphonyChatInput />}
        </>
    );
};

export default SymphonyNewChatPage;
